import React from 'react';

import { Loader } from '@/components/shared/Loader';
import { SettingsList, SettingsListItems } from '@/components/shared/SettingCard';
import UserAvatarGroup from '@/components/UserAvatar/UserAvatarGroup';
import { directMessagesUrl } from '@/config/urls';
import { useLeaveChatMutation } from '@/graphql';
import { FetchError } from '@/graphql/codegen-fetcher';
import { getChatTitleMembersRecipient } from '@/helpers/getChatChannelInfo';
import { useAuth } from '@/providers/AuthProvider';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';

import {
	EPrivateGroupSettingsDisplayState,
	IPropsPrivateGroupSettingsDisplay,
} from './PrivateGroupSettingsPanel';
import styles from './PrivateGroupSettingsRoot.module.scss';

export const PrivateGroupSettingsRoot: React.FC<
	IPropsPrivateGroupSettingsDisplay
> = (props) => {
	const { user } = useAuth();
	const { mutateAsync: leaveChat } = useLeaveChatMutation();
	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();

	const { userIds } = getChatTitleMembersRecipient(
		props.chat,
		user.id,
		user.displayName,
	);

	const memberCount = props.chat.userIds.length;

	const settings: SettingsListItems = [
		{
			type: 'setting',
			title: 'Edit group name',
			onClick: () =>
				props.pushDisplayState(
					EPrivateGroupSettingsDisplayState.EDIT_GROUP,
				),
		},
		{
			type: 'setting',
			title: 'See group members',
			onClick: () =>
				props.pushDisplayState(
					EPrivateGroupSettingsDisplayState.GROUP_MEMBERS,
				),
		},
		{
			type: 'setting',
			title: 'Leave group',
			onClick: async () => {
				try {
					await leaveChat({
						chatId: props.chat.id,
					});
					enqueueSnackbar('Successfully left group', {
						variant: 'success',
					});
					props.setOpen(false);
					navigate(directMessagesUrl);
				} catch (err) {
					if (err instanceof FetchError) {
						enqueueSnackbar(err.message, { variant: 'error' });
					}
				}
			},
			red: true,
		},
	];

	return (
		<div className={styles['private-group-settings-root']}>
			<div className={styles['private-group-settings-root__group']}>
				<div className={styles['private-group-settings-root__group-avatar']}>
					<UserAvatarGroup
						size={'x-large'}
						userIds={[userIds[0], userIds[1]]}
						activeSize="medium"
						userAvatarProps={{
							size: 'large',
							borderColor: 'body-background',
						}}
					/>
				</div>
				<div
					className={
						styles['private-group-settings-root__group-title']
					}
				>
					{props.loadingChatChannel ? <Loader /> : props.title}
				</div>
				<div
					className={
						styles['private-group-settings-root__group-member-count']
					}
				>
					{memberCount} {memberCount === 1 ? 'member' : 'members'}
				</div>
			</div>
			<div className="vsb-20" />
			<SettingsList items={settings} noChevron={true} />
		</div>
	);
};
